import { useMemo } from 'react';
import { getAthletes } from '../api/client';
import { useLoad } from '../hooks/useLoad';
import { usePageTitle } from '../hooks/usePageTitle';
import { useListFrom, useUrlParams } from '../hooks/useUrlParam';
import { useEntryState } from '../hooks/useEntryState';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import AthleteCard from '../components/shared/AthleteCard';
import FilterBar from '../components/shared/FilterBar';
import { PersonIcon } from '../components/shared/Icons';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import { nameMatches, textMatches } from '../utils/personName';
import { countryFilterOptions } from '../lib/countries';
import { BADGE_XS } from '../components/shared/badgeSize';

/** #552 — the list shows this many cards, and "Show more" adds this many. */
const PAGE_SIZE = 100;
export default function Athletes() {
  const { t } = useI18n();
  usePageTitle(t.nav.athletes);
  const { divisionLabel, countryName, athleteCount, teamCount } = useLabels();
  const { hasRole } = useAuthStore();
  // The archived records are for an admin and a team manager (the API refuses the others).
  const canReassign = hasRole('admin', 'team_manager');
  // #595 — the filters are in the URL, and each card is a link to the athlete
  // page that stores the URL of the list. The edit, the reassign and the merge
  // moved to the athlete page (operator, 2026-10-14).
  const listFrom = useListFrom();
  const [filters, setFilters] = useUrlParams({ q: '', gender: '', team: '', country: '', archived: '' });
  const { q: search, gender: filterGender, team: filterTeam, country: filterCountry } = filters;
  const showArchived = filters.archived === '1';
  // #552 — the page renders the first PAGE_SIZE cards only. The search and
  // the filters read the full list. #595 — the count belongs to the history
  // entry: Back shows the same rows, so the scroll restore can reach the old
  // position. A filter that the person changes starts again at one page.
  const [shown, setShown] = useEntryState('athletes-shown', PAGE_SIZE);
  const changeFilters = (patch: Partial<typeof filters>) => { setFilters(patch); setShown(PAGE_SIZE); };
  const setSearch = (v: string) => changeFilters({ q: v });
  const setFilterGender = (v: string) => changeFilters({ gender: v });
  const setFilterTeam = (v: string) => changeFilters({ team: v });
  const setFilterCountry = (v: string) => changeFilters({ country: v });
  const setArchivedParam = (v: string) => changeFilters({ archived: v });
  const clearFilters = () => changeFilters({ q: '', gender: '', team: '', country: '' });

  // #457 — a failed load shows an error with Retry, not the empty state ("No athletes").
  // #627, #665 (F02) — the newest load wins (useLoad). A tap on the archived
  // switch starts a second load, and the late answer of the first load showed
  // the wrong list. The old list stays on screen until the new answer.
  const includeInactive = showArchived && canReassign;
  const list = useLoad(
    () => getAthletes(includeInactive ? { includeInactive: true } : undefined).then(r => r.data),
    [showArchived, canReassign],
    { context: 'Athletes:list', initial: [], keepData: true },
  );
  const athletes = list.data;

  // Unique teams for filter
  const teams = useMemo(
    () => [...new Set(athletes.map(a => a.team_name).filter((c): c is string => !!c))].sort(),
    [athletes]
  );

  // Filtered list
  const filtered = useMemo(() => {
    let list = athletes;
    if (search) {
      // The name in either order (#479), or the team (#661, F30).
      list = list.filter(a =>
        nameMatches(search, a.first_name, a.last_name) ||
        textMatches(search, a.team_name)
      );
    }
    if (filterGender) list = list.filter(a => (a.gender || '').toLowerCase() === filterGender);
    if (filterTeam) list = list.filter(a => a.team_name === filterTeam);
    if (filterCountry) list = list.filter(a => a.country === filterCountry);
    return list;
  }, [athletes, search, filterGender, filterTeam, filterCountry]);


  // Stats
  const maleCount = athletes.filter(a => (a.gender || '').toLowerCase() === 'male').length;
  const femaleCount = athletes.filter(a => (a.gender || '').toLowerCase() === 'female').length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-black">{t.nav.athletes}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {athleteCount(athletes.length)} · {divisionLabel('male')}: {maleCount} · {divisionLabel('female')}: {femaleCount} · {teamCount(teams.length)}
        </p>
      </div>


      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t.checkin.searchPlaceholder}
        selects={[
          {
            value: filterGender,
            onChange: setFilterGender,
            options: [
              { value: 'male', label: divisionLabel('male') },
              { value: 'female', label: divisionLabel('female') },
            ],
            placeholder: t.common.allGenders,
          },
          {
            value: filterTeam,
            onChange: setFilterTeam,
            options: teams.map(c => ({ value: c, label: c })),
            placeholder: t.common.allTeams,
          },
          {
            value: filterCountry,
            onChange: setFilterCountry,
            options: countryFilterOptions(athletes.map(a => a.country), filterCountry, countryName),
            placeholder: t.country.all,
          },
        ]}
        onClear={clearFilters}
        showClear={!!(search || filterGender || filterTeam || filterCountry)}
        resultsCount={search || filterGender || filterTeam || filterCountry ? filtered.length : undefined}
      />

      {canReassign && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <label className="flex items-center gap-2 text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={showArchived} onChange={e => setArchivedParam(e.target.checked ? '1' : '')} className="rounded-sm"
              data-testid="athletes-show-archived" />
            {t.teams.showArchived}
          </label>
          {showArchived && (() => {
            const archivedCount = athletes.filter(a => a.is_active === false).length;
            return archivedCount === 0 ? (
              <span className="text-xs text-gray-400">No archived athletes</span>
            ) : (
              <span className="text-xs text-gray-500">{archivedCount} archived</span>
            );
          })()}
        </div>
      )}

      <AsyncContent
        loading={list.loading}
        error={!!list.error}
        onRetry={list.reload}
        isEmpty={filtered.length === 0}
        skeleton={<Skeleton.List shape="athlete-row" count={6} cols={3} />}
        empty={<EmptyState
          icon={<PersonIcon className="w-12 h-12" />}
          title={t.athletes.noAthletes}
          hint={t.common.empty.athletesHint}
        />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.slice(0, shown).map(a => (
            <AthleteCard
              key={`${a.source}-${a.id}`}
              athlete={a}
              showTeam
              to={`/athletes/${a.id}`}
              linkState={listFrom}
              extraBadges={a.is_active === false ? <span className={`badge badge-gray ${BADGE_XS}`}>{t.teams.archivedBadge}</span> : undefined}
            />
          ))}
        </div>
        {filtered.length > shown && (
          <button type="button" onClick={() => setShown(shown + PAGE_SIZE)}
            data-testid="athletes-show-more" className="btn-outline btn-sm w-full mt-3">
            {t.athletes.showMore.replace('{shown}', String(shown)).replace('{total}', String(filtered.length))}
          </button>
        )}
      </AsyncContent>

    </div>
  );
}
